/**
 * In Kano storefront search — match the product NAME and DESCRIPTION, and
 * nothing else.
 *
 * WHY. Same change Aska got on 2026-10-01, and the same evidence: Shopify's
 * storefront search matches its own synonym/fuzzy layer as well as every field,
 * so `dress` returned 148 products and `linen` 130. Shopify honours field
 * qualifiers inside `q`, and a qualified term switches that layer off, so each
 * word typed is sent as
 *
 *     title:<word> OR body:<word> OR variants.sku:<word>
 *
 * `body:` is the description; `description:` is NOT a qualifier and silently
 * degrades to an ordinary search. Measured on inkano.id: dress 148 -> 14,
 * linen 130 -> 28, neva tee 105 -> 8.
 *
 * WHAT THIS REPLACES. An earlier version (2026-09-21) scoped searches to
 * `title:word AND title:word` in assets/custom.js. It was title-only, it never
 * touched the as-you-type dropdown, and it rewrote the ADDRESS BAR — which
 * Andre ruled out on 2026-10-01: "i prefer the url not to change since i have
 * used it in many places including ads". That block is gone from custom.js; this
 * file is its replacement and the URL is never touched.
 *
 * TWO THINGS THE QUALIFIER COSTS, both handled here:
 *
 * 1. NO PARTIAL WORDS. A qualified term matches whole words only, and `*` does
 *    not help. So when the narrow search finds no product at all, the raw words
 *    are re-run and that answer is shown — the dropdown keeps answering while
 *    somebody is still typing.
 * 2. NO INDONESIAN. Aska needs only a colour list, because its descriptions are
 *    written in Bahasa and `body:` picks the garment words up by itself. In Kano
 *    writes its descriptions in ENGLISH ("Sleeveless dress with a gathered
 *    waistline"), so the words a shopper types in Bahasa have nothing to match:
 *    this list carries both colours and garments, and the English words in it
 *    come from In Kano's own 413 live titles.
 */
(() => {
  /** Bahasa -> the English words that appear in In Kano's titles. */
  const COLOURS = {
    hitam: ['black', 'charcoal', 'midnight'],
    putih: ['offwhite', 'white', 'ivory', 'cream', 'pearl'],
    krem: ['cream', 'beige', 'oat', 'oatmeal', 'butter', 'ivory'],
    coklat: ['coffee', 'walnut', 'peanut', 'cocoa', 'tan', 'chocolate'],
    cokelat: ['coffee', 'walnut', 'peanut', 'cocoa', 'tan', 'chocolate'],
    abu: ['grey', 'smoke', 'charcoal', 'slate', 'haze'],
    kelabu: ['grey', 'smoke', 'charcoal', 'slate'],
    biru: ['blue', 'sky', 'indigo', 'cobalt', 'denim', 'midnight'],
    navy: ['navy', 'indigo', 'midnight'],
    merah: ['maroon', 'red', 'brick'],
    pink: ['blush', 'pink', 'mallow', 'rose'],
    hijau: ['mint', 'sage', 'alpine', 'meadow', 'green', 'olive'],
    kuning: ['butter', 'mustard', 'yellow'],
    ungu: ['lilac', 'lavender', 'plum', 'purple', 'mallow'],
    emas: ['gold'],
    perak: ['silver'],
    // Garments, which Aska gets free from its Bahasa descriptions and this shop
    // does not. Keep it to words that genuinely name something in the catalogue.
    kaos: ['tee'],
    rok: ['skirt'],
    celana: ['pants'],
    jaket: ['jacket', 'outerwear'],
    kemeja: ['shirt'],
    atasan: ['top', 'tee'],
    kardigan: ['cardie', 'cardigan'],
    terusan: ['dress'],
    gamis: ['dress'],
    dalaman: ['inner'],
  };

  /** Already carries a field qualifier — somebody typed their own query. */
  const QUALIFIED = /(^|\s)(title|body|variants\.sku):/;
  /** Characters that would break Shopify's query parser rather than narrow it. */
  const UNSAFE = /["'()\[\]{}:*~^]/g;

  const group = (word) => {
    const alts = COLOURS[word.toLowerCase()] || [];
    const parts = [];
    for (const w of [word, ...alts]) parts.push(`title:${w}`, `body:${w}`);
    // Last, and after an OR, so the displayed terms can be rebuilt in Liquid by
    // taking the first token of each group. See sections/main-search.liquid.
    parts.push(`variants.sku:${word}`);
    return parts.join(' OR ');
  };

  /** The query to send, or null to leave Shopify's own search alone. */
  const qualify = (raw) => {
    const text = String(raw || '').trim();
    if (!text || QUALIFIED.test(text)) return null;
    const words = text.split(/\s+/).map((w) => w.replace(UNSAFE, '')).filter(Boolean);
    if (!words.length || words.some((w) => w.includes(':'))) return null;
    return words.map(group).join(' ');
  };

  /** Did a predictive-search answer contain any PRODUCT? */
  const hasProducts = (body) => {
    const text = String(body || '');
    if (text.trimStart().startsWith('{')) {
      try {
        return (JSON.parse(text)?.resources?.results?.products || []).length > 0;
      } catch {
        return true; // unreadable: treat as an answer rather than retrying blind
      }
    }
    // Broadcast's predictive section prints this list only when it has products.
    return text.includes('predictive-search__products__list');
  };

  // ---------------------------------------------------------------- dropdown
  //
  // Broadcast's <predictive-search> builds the URL itself inside theme.js and
  // reads the input's value as it is typed, so there is nothing to hook but the
  // request. Wrapping fetch also means the theme keeps its own abort and cache
  // behaviour, and a Prestige update cannot silently drop our change.
  const original = window.fetch;
  window.fetch = function (input, init) {
    try {
      if (typeof input === 'string' && input.includes('/search/suggest')) {
        const url = new URL(input, location.origin);
        const raw = url.searchParams.get('q');
        const narrowed = qualify(raw);
        if (narrowed) {
          url.searchParams.set('q', narrowed);
          return original.call(window, url.toString(), init).then(async (res) => {
            if (!res.ok) return original.call(window, input, init);
            const body = await res.text();
            if (hasProducts(body)) {
              return new Response(body, { status: res.status, statusText: res.statusText, headers: res.headers });
            }
            // Nothing by name — answer with the raw words, which is what keeps
            // a half-typed "chandan" from showing an empty dropdown.
            return original.call(window, input, init);
          });
        }
      }
    } catch {
      // A search is never worth breaking the page for.
    }
    return original.call(window, input, init);
  };

  // ------------------------------------------------------------ results page
  //
  // THE URL IS NOT TOUCHED (Andre, 2026-10-01: "i prefer the url not to change
  // since i have used it in many places including ads"). So /search?q=gamis is
  // still answered by Shopify's own broad search, and the narrow one is fetched
  // over the Section Rendering API and swapped into the page. Nothing intercepts
  // the form: it submits exactly the words that were typed, as it always did,
  // and an ad link that has been live for months lands on the same URL.
  //
  // main-search.liquid holds the broad grid back until this finishes, and shows
  // it unchanged if anything here declines to answer — a search that is merely
  // too broad is a worse outcome than no search at all, but only slightly.
  const reveal = (el) => {
    el?.classList.remove('kano-search-pending');
    document.querySelectorAll('.kano-search-waiting').forEach((w) => w.remove());
  };

  /** Pagination inside the swapped-in results must link back to HIS url shape. */
  const keepOurUrl = (root) => {
    root.querySelectorAll('a[href*="page="]').forEach((link) => {
      try {
        const page = new URL(link.getAttribute('href'), location.origin).searchParams.get('page');
        const ours = new URL(location.href);
        if (page) ours.searchParams.set('page', page);
        else ours.searchParams.delete('page');
        link.setAttribute('href', ours.toString());
      } catch {
        // Leave the theme's own link alone rather than break it.
      }
    });
  };

  const narrowThePage = async () => {
    const broad = document.querySelector('[data-kano-search]');
    if (!broad) return; // no results at all, or not a search page
    const narrowed = qualify(broad.getAttribute('data-kano-terms'));
    if (!narrowed) return reveal(broad); // already narrow, or nothing we handle

    const url = new URL('/search', location.origin);
    url.searchParams.set('q', narrowed);
    url.searchParams.set('section_id', broad.getAttribute('data-kano-search'));
    const page = new URL(location.href).searchParams.get('page');
    if (page) url.searchParams.set('page', page);

    try {
      const res = await original.call(window, url.toString());
      if (!res.ok) return reveal(broad);
      const fresh = new DOMParser()
        .parseFromString(await res.text(), 'text/html')
        .querySelector('[data-kano-search]');
      // No section, or a section holding no product: nothing matched the name or
      // the description, so Shopify's broad answer is the better one to show.
      if (!fresh || !fresh.querySelectorAll('.product-item').length) return reveal(broad);
      keepOurUrl(fresh);
      fresh.classList.remove('kano-search-pending');
      broad.replaceWith(fresh);
      document.querySelectorAll('.kano-search-waiting').forEach((w) => w.remove());
    } catch {
      reveal(broad);
    }
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', narrowThePage);
  } else {
    narrowThePage();
  }
})();
